import {
    Component,
    Input,
    OnInit,
    ViewEncapsulation,
    Inject,
    NgZone,
    ViewChild,
    AfterViewInit,
    HostListener,
    EventEmitter,
    Output,
    OnDestroy
} from '@angular/core';

// child directives
import { MapDirective } from './map.directive';
import { GlobeDirective } from './globe.directive';

@Component({
    selector: 'dashboard-main',
    template: `
        <div map-container
            [fullscreenEnabled]="fullscreenEnabled"
            [parent]="parent"
            [sites]="sites"
            [inboundStream]="inboundStream"
            [outboundStream]="outboundStream"
            [lateralStream]="lateralStream"
            [mode]="mode"
            [windowIsHidden]="windowIsHidden"
            (onMapDrag)="globe.onMapDrag($event)"
            (currentSiteIs)="currentSiteIs.emit($event)"
            (changeSite)="changeSite.emit($event)"
            [newStream]="newStream"
            class="map-container"></div>
        <div globe-container
            [inboundStream]="inboundStream"
            [outboundStream]="outboundStream"
            [newStream]="newStream"
            [mode]="mode"
            [parent]="parent"
            (onGlobeDrag)="map.onGlobeDrag($event)"
            (modeChanged)="changeMode($event)"
            class="globe-container">
            <canvas></canvas>
            <div class="toggles">
                <div (click)="globe.setGlobeMode('all')" [ngClass]="{'active': ( globe.mode === 'all') }" class="all">
                    All
                </div>
                <div (click)="globe.setGlobeMode('inbound')" [ngClass]="{'active': ( globe.mode === 'inbound') }" class="inbound">
                    Inbound
                </div>
                <div (click)="globe.setGlobeMode('outbound')" [ngClass]="{'active': ( globe.mode === 'outbound') }" class="outbound">
                    Outbound
                </div>
            </div>
        </div>
    `,
    // encapsulation: ViewEncapsulation.Native,
    styles: [
        require('css-to-string!../../../../node_modules/leaflet/dist/leaflet.css')
    ], // split up dash styles here
    directives: [
        MapDirective,
        GlobeDirective
    ],
    providers: [
        { provide: 'D3',  useValue: d3 }
    ]
})
export class DashboardMainComponent implements AfterViewInit, OnDestroy {

    // load in map methods
    @ViewChild(MapDirective) private map: MapDirective;
    // load in globe methods
    @ViewChild(GlobeDirective) private globe: GlobeDirective;

    // listen for window visible events
    private visiblilityChanges = new EventEmitter();
    private windowIsHidden = false;
    // note: this is tied to an observable so we can debounce the returns properly
    @HostListener('window:visibilitychange', ['$event'])
    onVisibilityChange(event) {
        this.visiblilityChanges.next(event);
    }

    // site changing events
    @Output() currentSiteIs = new EventEmitter();
    @Output() changeSite = new EventEmitter();
    @Input() private newStream;

    // parent div name
    @Input() private parent;
    // socket connector
    @Input() private sites;
    @Input() private fullscreenEnabled;
    // data streams
    @Input() private inboundStream;
    @Input() private outboundStream;
    @Input() private lateralStream;
    // mode (all, outboung, inbound)
    @Input() private mode;
    @Output() modeChanged = new EventEmitter();
    // timer helpers
    private currentTime = Date.now();
    private timerActive = true;
    constructor(
        @Inject('Window') private window,
        @Inject('Document') private document,
        @Inject('D3') private d3,
        private zone: NgZone
    ) {}
    /////////////////////
    // LIFESTYLE HOOKS //
    /////////////////////
    ngAfterViewInit() {
        // run the requestAnimationFrameSequence outside of angular
        this.zone.runOutsideAngular(() => {
            this.window.requestAnimationFrame(this.animate.bind(this))
        })
        // check to see if the window is being viewed when the event fires
        this.visiblilityChanges
            .debounceTime(200)
            .subscribe((event) => {
                this.windowIsHidden = this.isHiddenCheck();
            })
    }
    ngOnDestroy() {
        this.timerActive = false;
    }
    /////////////
    // METHODS //
    /////////////
    easeInOutCubic(t, b, c, d) {
        return c*((t=t/d-1)*t*t + 1) + b;
    }
    animate() {
        if (!this.timerActive) { return }
        if (!this.windowIsHidden) {
            // set up time stuff
            let time = this.window.Date.now();
            let diff = time - this.currentTime;
            let t = diff / 1000;
            t = (t > 1) ? 1 : t;

            // clear canvas
            this.map.context.clearRect(0, 0, this.map.width, this.map.height);

            // since animations are put into the array in cronological order,
            // we'll keep a drop count handy to wipe-left
            let cDrop = 0;
            for (let i = 0, len = this.map.animationsArray.length; i < len; i++) {
                // set start time if its new to the queue (before assigning it to a variable)
                if (this.map.animationsArray[i].start === 0) {
                    this.map.animationsArray[i].start = time;
                }
                // remove any items who's elapsed is time past the duration
                let item = this.map.animationsArray[i];
                let timePassed = time - item.start;
                // don't continue if timePassed is greater than duration and remove from array
                if (timePassed >= item.duration) {
                    cDrop++;

                    let eObj = {
                        radius: {
                            start: 2,
                            end: 40
                        },
                        opacity: 0.8,
                        duration: 3500,
                        start: time,
                        color: item.color,
                        x: item.x.end,
                        y: item.y.end
                    }
                    this.map.addExplosion(eObj);
                    continue
                }

                let ct = timePassed / item.duration;
                let cx = item.x.start + (ct * item.x.distance);
                let cy = item.y.start + (ct * item.y.distance);

                this.map.context.fillStyle = "rgba("+item.color+", 0.8)";
                this.map.context.save();
                this.map.context.translate(cx, cy);
                this.map.context.rotate(item.rotation);

                let scale = (ct < item.tmax) ? item.scale(ct) : 1;
                this.map.context.scale(scale, scale);
                this.map.context.beginPath();
                this.map.context.moveTo(118,3.1);
                this.map.context.bezierCurveTo(100.4,3.1,42.9,5,3.5,5);
                this.map.context.bezierCurveTo(2.1,5,1,4.1,1,3);
                this.map.context.bezierCurveTo(1,1.9000000000000004,2.1,1,3.5,1);
                this.map.context.bezierCurveTo(43.9,1,100.5,2.9,118,2.9);
                this.map.context.lineTo(118,3.1);
                this.map.context.closePath();
                this.map.context.fill();
                this.map.context.restore();
            }
            // iterate and cDrop items that are expired
            this.map.removeAnimations(cDrop);

            let eDrop = 0;
            // MAP EXPLODING CIRCLES
            for (let e = 0, len = this.map.explosionsArray.length; e < len; e++) {
                if (this.map.explosionsArray[e].start === 0) {
                    this.map.explosionsArray[e].start = time;
                }
                let item = this.map.explosionsArray[e];
                let timePassed = time - item.start;
                let et = timePassed / item.duration;
                // mark for deletion and stop if the item has reached its destination
                if (timePassed >= item.duration) {
                    eDrop++;
                    continue
                }
                // begin drawing connection
                this.map.context.beginPath();
                this.map.context.arc(
                    item.x,
                    item.y,
                    this.easeInOutCubic(timePassed, 10, 40, item.duration),
                    0,
                    2*this.window.Math.PI
                );
                this.map.context.fillStyle = "rgba("+item.color+", "+this.easeInOutCubic(timePassed, item.opacity, -item.opacity, item.duration),+")";
                this.map.context.fill();

            }
            this.map.removeExplosions(eDrop);


            // GLOBE
            // rotation
            if (this.globe.isInactive) {
                let dt = time - this.globe.rt;
                // get the new position from modified projection function
                this.globe.projection.rotate([this.globe.rotate[0] + this.globe.velocity[0] * dt, this.globe.rotate[1] + this.globe.velocity[1] * dt]);
            }
            // clear globe box
            this.globe.context.clearRect(0, 0, this.globe.box.width, this.globe.box.height);
            let radius = this.globe.radius < 1 ? 1 : this.globe.radius;
            // add background circle for globe
            this.globe.context.beginPath();
            this.globe.context.arc(this.globe.box.width / 2, this.globe.box.height / 2, radius+4, 0, 2 * this.window.Math.PI, true);
            this.globe.context.fillStyle = this.globe.backgroundColor;
            this.globe.context.fill();
            this.globe.context.closePath();


            // set colors
            this.globe.context.fillStyle = this.globe.foregroundColor;
            this.globe.context.strokeStyle = this.globe.strokeColor;

            this.globe.context.beginPath();
            this.globe.path(this.globe.land);
            this.globe.context.fill();

            // loop through active countries and color them
            for (let h = 0, len = this.globe.heatmapArray.length; h < len; h++) {
                this.globe.context.beginPath();
                this.globe.context.fillStyle = this.r2h(
                    this.interpolateColor(this.globe.heatmapArray[h].colors.start, this.globe.heatmapArray[h].colors.end, t)
                )
                this.globe.path(this.globe.heatmapArray[h].path);
                this.globe.context.fill();
                this.globe.context.closePath();
            }

            // globe constant was tweaked from window width that the circle on the globe
            // roughly matches the viewport in the Leaflet map.
            // Compute the size of the current viewport on the globe.
            // Draws from formula found at
            // http://wiki.openstreetmap.org/wiki/Zoom_levels
            let currentViewRadius = this.globe.windowWidth * this.globe.box.height * this.globe.cosLat / (this.window.Math.pow(2, this.globe.zoom + 8));
            // Stop the code from crashing with transient states that happen on page load.
            currentViewRadius = currentViewRadius < 1 ? 1 : currentViewRadius;
            this.globe.context.beginPath();
            this.globe.context.arc(this.globe.box.width / 2, this.globe.box.height / 2, radius+4, 0, 2 * this.window.Math.PI, true);
            this.globe.context.lineWidth = 2.5;
            this.globe.context.stroke();
            // globe makes the circle invert whatever color is underneath it.
            if (!this.globe.isInactive && (currentViewRadius < (this.globe.box.width / 2))) {
                this.globe.context.save();
                this.globe.context.globalCompositeOperation = "difference";
                this.globe.context.strokeStyle = "white";
                // Draw the circle that represents the current zoom and pan.
                this.globe.context.beginPath();
                this.globe.context.arc(this.globe.box.width / 2, this.globe.box.height / 2, currentViewRadius, 0, 2 * this.window.Math.PI, true);
                this.globe.context.lineWidth = 2.5;
                this.globe.context.stroke();
                // globe is the inverse of this.globe.context.save(), it pops off the this.globe.context stack so
                // we get back whatever the previous value was for
                // globalCompositeOperation.
                this.globe.context.restore();
            }

            // DATA handling stuff here
            if (t === 1) {
                this.currentTime = time;
                this.globe.processData();
                // check if globe is inactive
                this.globe.checkInactive();
            }

        }
        if (this.timerActive)
        this.window.requestAnimationFrame(this.animate.bind(this));
    }
    h2r(hex) {
        // Converts a #ffffff hex string into an [r,g,b] array
        var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? [
            this.window.parseInt(result[1], 16),
            this.window.parseInt(result[2], 16),
            this.window.parseInt(result[3], 16)
        ] : null;
    }
    r2h(rgb) {
        // Inverse of the above
        return "#" + ((1 << 24) + (rgb[0] << 16) + (rgb[1] << 8) + rgb[2]).toString(16).slice(1);
    }
    interpolateColor(color1, color2, factor) {
        // Interpolates two [r,g,b] colors and returns an [r,g,b] of the result
        color1 = this.h2r(color1);
        color2 = this.h2r(color2);
        if (arguments.length < 3) { factor = 0.5; }
        var result = color1.slice();
        for (var i=0;i<3;i++) {
            result[i] = Math.round(result[i] + factor*(color2[i]-color1[i]));
        }
        return result;
    }
    // check to see if the page is hidden so we dont waste cpu resources
    getHiddenProperites(){
        let prefixes = ['webkit','moz','ms','o'];
        // if 'hidden' is natively supported just return it
        if ('hidden' in this.document) return 'hidden';
        // otherwise loop over all the known prefixes until we find one
        for (let i = 0; i < prefixes.length; i++){
            if ((prefixes[i] + 'Hidden') in this.document)
                return prefixes[i] + 'Hidden';
        }
        // otherwise it's not supported
        return null;
    }
    isHiddenCheck() {
        let prop = this.getHiddenProperites();
        if (!prop) return false;
        return this.document[prop];
    }
    changeMode (newMode) {
        this.mode = newMode.value;
    }
}



/** WEBPACK FOOTER **
 ** ./src/landing/directives/dashboard/dashboard.main.component.ts
 **/